import { useMutation, useQuery } from '@apollo/client';
import { Laptop, Monitor, Smartphone, Tablet } from 'lucide-react';
import { useState } from 'react';
import {
  GET_MY_SESSIONS,
  REVOKE_ALL_OTHER_SESSIONS,
  REVOKE_SESSION,
} from '../../../graphql/resolvers/sessions/sessions.gql';
import { formatDate } from '../../../helpers/dateConverters';
import ButtonComponent from '../../UI/Button/Button.component';
import { SimpleDialog } from '../../UI/SimpleDialog';
import './devices-view.scss';

const deviceIcon = (deviceType: string) => {
  switch (deviceType) {
    case 'mobile':
      return <Smartphone size={20} />;
    case 'tablet':
      return <Tablet size={20} />;
    case 'desktop':
      return <Laptop size={20} />;
    default:
      return <Monitor size={20} />;
  }
};

/**
 * TRIL-377: Settings → Devices. Lists the caller's active sessions (`mySessions`)
 * and offers remote logout of one (`revokeSession`) or all others
 * (`revokeAllOtherSessions`). The "This device" badge is a client-side compare of
 * the stored `sessionId` (written at login/refresh) — no server role check.
 */
export function DevicesView() {
  const currentSessionId =
    typeof window !== 'undefined'
      ? window.localStorage.getItem('sessionId')
      : null;

  const { data, loading, error, refetch } = useQuery(GET_MY_SESSIONS, {
    fetchPolicy: 'cache-and-network',
  });
  const [revokeSession, { loading: revoking }] = useMutation(REVOKE_SESSION);
  const [revokeAllOthers, { loading: revokingAll }] = useMutation(
    REVOKE_ALL_OTHER_SESSIONS,
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const sessions = data?.mySessions ?? [];
  const otherCount = sessions.filter(
    (s) => s.sessionId !== currentSessionId,
  ).length;

  const handleRevoke = async (sessionId: string) => {
    await revokeSession({ variables: { sessionId } });
    await refetch();
  };

  const handleRevokeAll = async () => {
    // The server refuses an empty currentSessionId; guard the UI too so the
    // action is only offered when we can identify (and spare) this device.
    if (!currentSessionId) return;
    await revokeAllOthers({ variables: { currentSessionId } });
    setConfirmOpen(false);
    await refetch();
  };

  return (
    <section className="DEVICES__VIEW">
      <header className="DEVICES__VIEW__header">
        <div>
          <h2 className="DEVICES__VIEW__title">Devices &amp; sessions</h2>
          <p className="DEVICES__VIEW__subtitle">
            Places you're signed in. Sign out of any you don't recognize.
          </p>
        </div>
        <ButtonComponent
          type="button"
          variant="dangerOutline"
          disabled={!currentSessionId || otherCount === 0 || revokingAll}
          onClick={() => setConfirmOpen(true)}
        >
          Sign out everywhere else
        </ButtonComponent>
      </header>

      {loading && sessions.length === 0 ? (
        <p className="DEVICES__VIEW__state">Loading sessions…</p>
      ) : error ? (
        <p className="DEVICES__VIEW__state">Couldn't load your sessions.</p>
      ) : sessions.length === 0 ? (
        <p className="DEVICES__VIEW__state">No active sessions.</p>
      ) : (
        <ul className="DEVICES__LIST">
          {sessions.map((s) => {
            const isCurrent = s.sessionId === currentSessionId;
            return (
              <li key={s.sessionId} className="DEVICE__ROW">
                <span className="DEVICE__ROW__icon">
                  {deviceIcon(s.deviceType)}
                </span>
                <div className="DEVICE__ROW__main">
                  <p className="DEVICE__ROW__name">
                    {s.browser} on {s.os}
                    {isCurrent && (
                      <span className="DEVICE__ROW__badge">This device</span>
                    )}
                  </p>
                  <p className="DEVICE__ROW__meta">
                    {s.ip ?? 'Unknown location'} · Last active{' '}
                    {s.lastSeenAt ? formatDate(s.lastSeenAt) : 'unknown'}
                  </p>
                </div>
                {!isCurrent && (
                  <ButtonComponent
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={revoking}
                    onClick={() => handleRevoke(s.sessionId)}
                  >
                    Sign out
                  </ButtonComponent>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <SimpleDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onSubmit={handleRevokeAll}
        dialogTitle="Sign out everywhere else?"
        dialogBody={
          <p>
            This signs out your other {otherCount}{' '}
            {otherCount === 1 ? 'session' : 'sessions'}. This device stays
            signed in.
          </p>
        }
        actionLabel="Sign out others"
        closeModalLabel="Cancel"
      />
    </section>
  );
}

export default DevicesView;
